import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import styles from './AdditionalInfo.module.css';

interface CardUpdateLinkResponse {
  link: string;
  existing: boolean;
}

interface CardUpdateLinkProps {
  fundId: number;
  standingOrderId: number;
  mosadSource: string;
  /** wa.me needs the donor's number already in international form; absent when we have none. */
  whatsappNumber?: string | null;
  email?: string | null;
}

/**
 * A link the donor opens to put a new card on this standing order.
 *
 * Until now a card that expired ended the standing order in practice: the fund could see the
 * rejections but had no way to collect a new number, so the donor had to be walked through
 * setting the whole order up again and the payments in between were simply lost. Nedarim host
 * the page and the donor types the number into it, so no card number passes through this app.
 *
 * Sending it is the fund's job - Nedarim only mint the address - so the link comes with the two
 * channels a coordinator actually chases donors on, and a copy button for everything else.
 */
const CardUpdateLink = ({
  fundId, standingOrderId, mosadSource, whatsappNumber, email,
}: CardUpdateLinkProps) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<CardUpdateLinkResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = `nedarim-standing-orders/${fundId}/credit/${standingOrderId}/card-update-link?mosadSource=${mosadSource}`;
  const { mutate: createLink, isPending } = usePostAuthApi<CardUpdateLinkResponse, object>(url, {
    onSuccess: (data) => {
      setFailed(false);
      setResult(data);
    },
    onError: () => {
      setResult(null);
      setFailed(true);
    },
  });

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // An http origin or a browser that refuses the clipboard: the link is on screen and
      // selectable, so say nothing rather than raise an error about a convenience.
    }
  };

  if (!result) {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          className={styles.cardUpdateButton}
          disabled={isPending}
          onClick={() => createLink({})}
        >
          {isPending ? t('standingOrders.cardUpdate.creating') : t('standingOrders.cardUpdate.create')}
        </button>
        {failed && (
          <span className={styles.cardUpdateError}>{t('standingOrders.cardUpdate.error')}</span>
        )}
      </div>
    );
  }

  const message = t('standingOrders.cardUpdate.message', { link: result.link });

  return (
    <div className="flex flex-col gap-2">
      <span className="body-md-regular text-(--font-body-3)">
        {result.existing
          ? t('standingOrders.cardUpdate.existing')
          : t('standingOrders.cardUpdate.created')}
      </span>

      <code className={styles.cardUpdateLink}>{result.link}</code>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={styles.cardUpdateButton} onClick={handleCopy}>
          {copied ? t('standingOrders.cardUpdate.copied') : t('standingOrders.cardUpdate.copy')}
        </button>

        {whatsappNumber && (
          <a
            className={styles.cardUpdateButton}
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('standingOrders.cardUpdate.sendWhatsapp')}
          </a>
        )}

        {email && (
          <a
            className={styles.cardUpdateButton}
            href={`mailto:${email}?subject=${encodeURIComponent(t('standingOrders.cardUpdate.mailSubject'))}&body=${encodeURIComponent(message)}`}
          >
            {t('standingOrders.cardUpdate.sendMail')}
          </a>
        )}
      </div>

      <span className="body-sm-regular text-(--font-body-3)">
        {t('standingOrders.cardUpdate.validity')}
      </span>
    </div>
  );
};

export default CardUpdateLink;
